import { useCallback, useMemo, useState } from "react";
import { PackingKit } from "~/data/packingKits.ts";
import { useSpace } from "~/providers/SpaceContext.ts";
import { runBusyOperation } from "~/services/busyOperation.ts";
import { runBusyTasks } from "~/services/busyTasks.ts";
import { type WriteDb } from "~/services/database.ts";
import { UNCATEGORIZED } from "~/services/utils.ts";
import { DuplicateNameError } from "~/types/DuplicateNameError.ts";
import { Image } from "~/types/Image.ts";
import { NamedEntity } from "~/types/NamedEntity.ts";
import { PackItem } from "~/types/PackItem.ts";
import { hasDuplicateEntityName } from "../shared/entityValidation.ts";
import { ImageViewerModal } from "../shared/ImageViewerModal.tsx";
import { useEntityImageActions } from "../shared/useEntityImageActions.ts";
import { FilterSheet } from "./FilterSheet.tsx";
import { applyFilters } from "./filterUtils.ts";
import { type AddItemDialogState, ItemsPanel, type ListHandlers, type TextDialogState } from "./ItemsPanel.tsx";
import {
  useAssignMembers,
  useCategoryRename,
  useCopyToList,
  useItemDelete,
  useItemRename,
  useListRenamer,
  useMoveCategory,
  useMoveItemsToCategory,
  useSortCategoryAlpha,
} from "./itemHandlers.ts";
import { getNextCategoryRank, getNextItemRank, getTopItemRank } from "./itemsSectionHelpers.ts";
import { KitPickerModal } from "./KitPickerModal.tsx";
import { ListNotesSheet, type ListNotesState } from "./ListNotesSheet.tsx";
import { animateLayout } from "./layoutAnimation.ts";
import { HOME_COPY } from "./styles.ts";
import { ItemsSectionProps } from "./types.ts";
import { useBulkEdit } from "./useBulkEdit.ts";
import { useFilterDialog } from "./useFilterDialog.ts";
import { useOptimisticItems } from "./useOptimisticItems.ts";
import { useSearch } from "./useSearch.ts";

const getCategoriesInList = (categories: NamedEntity[], items: PackItem[]) => {
  const categoryIds = new Set(items.map((i) => i.category));
  const result = categories.filter((c) => categoryIds.has(c.id));
  if (categoryIds.has("") && result.length > 0) result.push(UNCATEGORIZED);
  return result.sort((a, b) => b.rank - a.rank);
};

const buildImageMap = (images: Image[], type: string) =>
  new Map(images.filter((image) => image.type === type).map((image) => [image.typeId, image.url]));

const attachImagesToEntities = (entities: NamedEntity[], imageMap: Map<string, string>) =>
  entities.map((entity) => ({ ...entity, image: imageMap.get(entity.id) }));

export const ItemsSection = (props: ItemsSectionProps) => {
  const { profile, writeDb } = useSpace();
  const imageDb = {
    add: writeDb.addImage,
    update: writeDb.updateImage,
    delete: writeDb.deleteImage,
  };
  const imageActions = useEntityImageActions("packingLists", imageDb);
  const itemImageActions = useEntityImageActions("packItems", imageDb);
  const list = props.selection.selectedList;
  const { optimisticItems, toggleCategory, toggleItem, toggleMemberPacked, toggleAllMembers, undo, canUndo } =
    useOptimisticItems(props.itemsState.items, list?.id);
  const categoryImageMap = useMemo(
    () => buildImageMap(props.imagesState.images, "categories"),
    [props.imagesState.images]
  );
  const memberImageMap = useMemo(() => buildImageMap(props.imagesState.images, "members"), [props.imagesState.images]);
  const categoriesInList = useMemo(
    () =>
      attachImagesToEntities(getCategoriesInList(props.categoriesState.categories, optimisticItems), categoryImageMap),
    [props.categoriesState.categories, optimisticItems, categoryImageMap]
  );
  const membersWithImages = useMemo(
    () => attachImagesToEntities(props.membersState.members, memberImageMap),
    [props.membersState.members, memberImageMap]
  );
  const filterDialog = useFilterDialog(categoriesInList, membersWithImages, optimisticItems, list?.id);
  const filteredItems = useMemo(
    () =>
      applyFilters(
        optimisticItems,
        filterDialog.selectedCategories,
        filterDialog.selectedMembers,
        filterDialog.statusFilter
      ),
    [optimisticItems, filterDialog.selectedCategories, filterDialog.selectedMembers, filterDialog.statusFilter]
  );
  const filteredItemsState = {
    ...props.itemsState,
    items: filteredItems,
    hasItems: optimisticItems.length > 0,
    filteredEmpty: optimisticItems.length > 0 && filteredItems.length === 0,
  };
  const filteredProps = { ...props, itemsState: filteredItemsState };
  const search = useSearch(filteredItems, categoriesInList);
  const handlers = useItemsSectionHandlers(
    toggleItem,
    toggleCategory,
    toggleMemberPacked,
    toggleAllMembers,
    optimisticItems,
    props.categoriesState.categories,
    props.lists
  );
  const renameList = useListRenamer();
  const addItemDialog = useAddItemDialog(
    optimisticItems,
    props.categoriesState.categories,
    writeDb,
    profile?.addNewItemsOnTop ?? false,
    list?.id
  );
  const renameDialog = useRenameDialog(list, props.lists, renameList);
  const notesSheet = useListNotes(list, writeDb);
  const bulkEdit = useBulkEdit(optimisticItems, writeDb);
  if (!list) return null;
  const displayName = list.name?.trim() ? list.name : HOME_COPY.detailHeader;
  const listImage = props.imagesState.images.find((img) => img.type === "packingLists" && img.typeId === list.id);
  return (
    <>
      <ItemsPanel
        {...filteredProps}
        {...handlers}
        allItems={optimisticItems}
        list={list}
        displayName={displayName}
        listImageUrl={listImage?.url}
        listImageLoading={imageActions.loadingEntityId === list.id}
        onListImagePress={() => imageActions.handleImagePress(list.id, listImage)}
        onItemImagePress={(item) => {
          const existing = props.imagesState.images.find((img) => img.type === "packItems" && img.typeId === item.id);
          itemImageActions.handleImagePress(item.id, existing);
        }}
        renameDialog={renameDialog}
        addItemDialog={addItemDialog}
        filterDialog={filterDialog}
        search={search}
        notesSheet={notesSheet}
        onBulkEdit={bulkEdit.open}
        bulkEditing={bulkEdit.busy}
        onUndo={undo}
        canUndo={canUndo}
      />
      <FilterSheet
        visible={filterDialog.visible}
        categories={filterDialog.categories}
        selectedCategories={filterDialog.selectedCategories}
        onToggleCategory={filterDialog.onToggleCategory}
        members={filterDialog.members}
        selectedMembers={filterDialog.selectedMembers}
        onToggleMember={filterDialog.onToggleMember}
        statusFilter={filterDialog.statusFilter}
        onSetStatus={filterDialog.onSetStatus}
        onClear={filterDialog.onClear}
        onClose={filterDialog.close}
        shownCount={filteredItems.length}
        totalItemCount={optimisticItems.length}
      />
      <KitPickerModal
        visible={addItemDialog.kitPickerVisible}
        onClose={addItemDialog.closeKitPicker}
        onAdd={addItemDialog.addKits}
      />
      <ListNotesSheet state={notesSheet} />
      {imageActions.viewerState && (
        <ImageViewerModal
          visible={true}
          imageUrl={imageActions.viewerState.image.url}
          loading={imageActions.modalLoading}
          connectedLabel={displayName}
          textValue={imageActions.textValue}
          textSubmitDisabled={!imageActions.textValue.trim()}
          onTextChange={imageActions.setTextValue}
          onTextSubmit={() => void imageActions.submitText()}
          onClose={imageActions.closeViewer}
          onReplace={imageActions.handleReplace}
          onRemove={imageActions.handleRemove}
        />
      )}
      {itemImageActions.viewerState && (
        <ImageViewerModal
          visible={true}
          imageUrl={itemImageActions.viewerState.image.url}
          loading={itemImageActions.modalLoading}
          connectedLabel={optimisticItems.find((i) => i.id === itemImageActions.viewerState?.entityId)?.name}
          textValue={itemImageActions.textValue}
          textSubmitDisabled={!itemImageActions.textValue.trim()}
          onTextChange={itemImageActions.setTextValue}
          onTextSubmit={() => void itemImageActions.submitText()}
          onClose={itemImageActions.closeViewer}
          onReplace={itemImageActions.handleReplace}
          onRemove={itemImageActions.handleRemove}
        />
      )}
    </>
  );
};

type ToggleItem = (item: PackItem) => void;
type ToggleCategory = (items: PackItem[], checked: boolean) => void;

const useItemsSectionHandlers = (
  toggleItem: ToggleItem,
  toggleCategory: ToggleCategory,
  toggleMemberPacked: (item: PackItem, memberId: string) => void,
  toggleAllMembers: (item: PackItem, checked: boolean) => void,
  items: PackItem[],
  categories: NamedEntity[],
  lists: NamedEntity[]
): ListHandlers => {
  const renameItem = useItemRename();
  const deleteItem = useItemDelete();
  const renameCategory = useCategoryRename();
  const assignMembers = useAssignMembers();
  const moveCategory = useMoveCategory();
  const moveItemsToCategory = useMoveItemsToCategory();
  const copyToList = useCopyToList();
  const sortCategoryAlpha = useSortCategoryAlpha();

  return {
    onToggle: useCallback(
      (item: PackItem) => {
        void toggleItem(item);
      },
      [toggleItem]
    ),
    onRenameItem: renameItem,
    onDeleteItem: useCallback(
      (id: string) => {
        void deleteItem(items, categories, lists, id);
      },
      [categories, deleteItem, items, lists]
    ),
    onRenameCategory: renameCategory,
    onToggleCategory: useCallback(
      (items: PackItem[], checked: boolean) => {
        void toggleCategory(items, checked);
      },
      [toggleCategory]
    ),
    onAssignMembers: assignMembers,
    onToggleMemberPacked: toggleMemberPacked,
    onToggleAllMembers: toggleAllMembers,
    onMoveCategory: moveCategory,
    onMoveItemsToCategory: moveItemsToCategory,
    onCopyToList: copyToList,
    onSortCategoryAlpha: sortCategoryAlpha,
  };
};

const useRenameDialog = (
  list: NamedEntity | null,
  lists: NamedEntity[],
  rename: (target: NamedEntity, name: string) => void
): TextDialogState => {
  const [visible, setVisible] = useState(false);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const open = useCallback(() => {
    setValue(list?.name ?? "");
    setError(null);
    setVisible(true);
  }, [list?.name]);
  const close = useCallback(() => setVisible(false), []);
  const getError = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return HOME_COPY.nameRequired;
      const isDuplicate = list && trimmed !== list.name && hasDuplicateEntityName(trimmed, lists, list.id);
      return isDuplicate ? HOME_COPY.duplicateListName : null;
    },
    [list, lists]
  );
  const onChange = useCallback(
    (text: string) => {
      setValue(text);
      setError(getError(text));
    },
    [getError]
  );
  const submitText = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      const nextError = getError(text);
      if (!list || !trimmed || trimmed === list.name || nextError) {
        setError(nextError);
        if (!nextError) close();
        return;
      }
      rename(list, trimmed);
      close();
    },
    [close, getError, list, rename]
  );
  const submit = useCallback(() => {
    submitText(value);
  }, [submitText, value]);
  return {
    visible,
    value,
    error,
    getError,
    setValue: onChange,
    open,
    close,
    submitText,
    submit,
  };
};

const useAddItemDialog = (
  items: PackItem[],
  categories: NamedEntity[],
  writeDb: WriteDb,
  addNewItemsOnTop: boolean,
  listId?: string | null
): AddItemDialogState => {
  const [visible, setVisible] = useState(false);
  const [kitPickerVisible, setKitPickerVisible] = useState(false);
  const [initialCategory, setInitialCategory] = useState<NamedEntity | undefined>();
  const [lastSelectedCategoryId, setLastSelectedCategoryId] = useState(UNCATEGORIZED.id);
  const hasUncategorizedItems = useMemo(() => items.some((i) => !i.category), [items]);
  const firstUsedCategory = useMemo(() => {
    const usedIds = new Set(items.map((i) => i.category).filter(Boolean));
    return categories.find((c) => usedIds.has(c.id));
  }, [items, categories]);
  const resolveCategory = useCallback(
    (category?: NamedEntity) => {
      const resolved = category ?? categories.find((entry) => entry.id === lastSelectedCategoryId);
      if (resolved) return resolved;
      return !hasUncategorizedItems && firstUsedCategory ? firstUsedCategory : UNCATEGORIZED;
    },
    [categories, lastSelectedCategoryId, hasUncategorizedItems, firstUsedCategory]
  );
  const open = useCallback(
    (category?: NamedEntity) => {
      setInitialCategory(resolveCategory(category));
      setVisible(true);
    },
    [resolveCategory]
  );
  const close = useCallback(() => setVisible(false), []);
  const onBrowseKits = useCallback(() => {
    setVisible(false);
    setKitPickerVisible(true);
  }, []);
  const closeKitPicker = useCallback(() => setKitPickerVisible(false), []);
  const submit = useCallback(
    async (itemName: string, category: NamedEntity | null, newCategoryName: string | null, keepOpen: boolean) => {
      if (!listId) return UNCATEGORIZED;
      animateLayout();
      let categoryId = category?.id ?? UNCATEGORIZED.id;
      let nextCategory = category ?? UNCATEGORIZED;
      if (newCategoryName) {
        const existing = categories.find((c) => c.name.toLowerCase() === newCategoryName.trim().toLowerCase());
        nextCategory = existing ?? (await writeDb.addCategory(newCategoryName, getNextCategoryRank(categories)));
        categoryId = nextCategory.id;
      }
      setLastSelectedCategoryId(categoryId);
      const nextItemRank = addNewItemsOnTop ? getTopItemRank(items) : getNextItemRank(items);
      await writeDb.addPackItem(itemName, [], categoryId, listId, nextItemRank);
      if (!keepOpen) close();
      return nextCategory;
    },
    [items, categories, writeDb, addNewItemsOnTop, listId, close]
  );
  const addKits = useCallback(
    (kits: PackingKit[]) =>
      runBusyOperation(
        async () => {
          if (!listId) return;
          animateLayout();
          const categoryMap = new Map<string, string>();
          for (const cat of categories) {
            categoryMap.set(cat.name.toLowerCase(), cat.id);
          }
          const totalKitItems = kits.reduce((count, kit) => count + kit.items.length, 0);
          let currentCategoryRank = getNextCategoryRank(categories);
          let currentItemRank = addNewItemsOnTop ? getTopItemRank(items, totalKitItems) : getNextItemRank(items);
          await runBusyTasks(
            kits.flatMap((kit) => kit.items),
            async (kitItem) => {
              const catKey = kitItem.category.toLowerCase();
              let categoryId = categoryMap.get(catKey);
              if (!categoryId) {
                try {
                  const newCat = await writeDb.addCategory(kitItem.category, currentCategoryRank++);
                  categoryId = newCat.id;
                  categoryMap.set(catKey, categoryId);
                } catch (e) {
                  if (!(e instanceof DuplicateNameError)) throw e;
                  return;
                }
              }
              try {
                await writeDb.addPackItem(kitItem.name, [], categoryId, listId, currentItemRank);
                currentItemRank -= 1;
              } catch (e) {
                if (!(e instanceof DuplicateNameError)) throw e;
              }
            },
            "addingKits"
          );
        },
        { message: "addingKits" }
      ),
    [items, categories, writeDb, addNewItemsOnTop, listId]
  );
  return {
    visible,
    initialCategory,
    open,
    close,
    submit,
    onBrowseKits,
    kitPickerVisible,
    closeKitPicker,
    addKits,
  };
};

const useListNotes = (list: NamedEntity | null, writeDb: WriteDb): ListNotesState => {
  const [visible, setVisible] = useState(false);
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotesLocal] = useState(false);

  const open = useCallback(() => {
    setNotes(list?.notes ?? "");
    setShowNotesLocal(list?.showNotes ?? false);
    setVisible(true);
  }, [list?.notes, list?.showNotes]);

  const persist = useCallback(async () => {
    if (!list) return;
    await writeDb.updatePackingListInfo(list.id, notes, showNotes, list.dueAt ?? null);
  }, [list, notes, showNotes, writeDb]);

  const updateNotes = useCallback(
    (value: string) => {
      setNotes(value);
      if (list) void writeDb.updatePackingListInfo(list.id, value, showNotes, list.dueAt ?? null);
    },
    [list, showNotes, writeDb]
  );

  const close = useCallback(() => {
    setVisible(false);
    void persist();
  }, [persist]);

  const setShowNotes = useCallback(
    (value: boolean) => {
      setShowNotesLocal(value);
      if (list) void writeDb.updatePackingListInfo(list.id, notes, value, list.dueAt ?? null);
    },
    [list, notes, writeDb]
  );

  return { visible, notes, showNotes, open, close, setNotes: updateNotes, setShowNotes };
};
